import React, { useState, useEffect } from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, runTransaction, increment, getDoc } from 'firebase/firestore';
import { db } from '../App';
import { useFireAuth } from '../FireAuthContext';
import './Bank.css';

const MAX_TRANSFER = 1_000_000;

const Bank = () => {
  const { user } = useFireAuth();
  const [amount, setAmount] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [currency, setCurrency] = useState('money');
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [bankMessage, setBankMessage] = useState('');
  const navigate = useNavigate();

  const validateAmount = (amt) => {
    if (isNaN(amt) || amt <= 0) return 'Amount must be a positive number.';
    if (amt < 0) return 'Amount cannot be negative.';
    if (currency !== 'item' && amt > MAX_TRANSFER) return `Amount cannot exceed ${MAX_TRANSFER.toLocaleString()}.`;
    return null;
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        membershipNumber: doc.data().membershipNumber
      })).sort((a, b) => a.name.localeCompare(b.name));
      setUsers(usersList);
    };

    const fetchItems = async () => {
      const itemsCollection = collection(db, 'items');
      const itemsSnapshot = await getDocs(itemsCollection);
      const itemsList = itemsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      })).sort((a, b) => a.name.localeCompare(b.name));
      setItems(itemsList);
    };

    fetchUsers();
    fetchItems();
  }, []);

  const handleUserChange = async (userId) => {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      setSelectedUser({ id: userId, ...userDoc.data() });
    }
  };

  const generateUniqueId = () => {
    return Math.floor(Math.random() * 9000000000000) + 1000000000000;
  };

  const handleDeposit = async () => {
    const amt = parseFloat(amount);
    setBankMessage('');
    if (!selectedUser) { setBankMessage('Please select a user.'); return; }
    if (currency === 'item' && !selectedItem) { setBankMessage('Please select an item.'); return; }
    const validationError = validateAmount(amt);
    if (validationError) { setBankMessage(validationError); return; }

    try {
      const userDocRef = doc(db, 'users', selectedUser.id);
      if (currency === 'money') {
        await runTransaction(db, async (transaction) => {
          transaction.update(userDocRef, { money: increment(amt) });
        });
      } else if (currency === 'hahumCoin') {
        await runTransaction(db, async (transaction) => {
          transaction.update(userDocRef, { 'crypto.hahumCoin': increment(amt) });
        });
      } else if (currency === 'item') {
        await runTransaction(db, async (transaction) => {
          const userDoc = await transaction.get(userDocRef);
          const userInventory = userDoc.data().inventory || [];
          const updatedInventory = [
            ...userInventory,
            ...Array.from({ length: amt }, () => `${generateUniqueId()}_${selectedItem}`)
          ];
          transaction.update(userDocRef, { inventory: updatedInventory });
        });
      }
      setAmount('');
      setSelectedItem('');
      setBankMessage('Deposit successful.');
      await handleUserChange(selectedUser.id);
    } catch (error) {
      console.error('Error during deposit:', error);
      setBankMessage('Deposit failed. Please try again.');
    }
  };

  const handleWithdraw = async () => {
    const amt = parseFloat(amount);
    setBankMessage('');
    if (!selectedUser) { setBankMessage('Please select a user.'); return; }
    if (currency === 'item' && !selectedItem) { setBankMessage('Please select an item.'); return; }
    const validationError = validateAmount(amt);
    if (validationError) { setBankMessage(validationError); return; }

    try {
      const userDocRef = doc(db, 'users', selectedUser.id);
      if (currency === 'money') {
        await runTransaction(db, async (transaction) => {
          transaction.update(userDocRef, { money: increment(-amt) });
        });
      } else if (currency === 'hahumCoin') {
        await runTransaction(db, async (transaction) => {
          transaction.update(userDocRef, { 'crypto.hahumCoin': increment(-amt) });
        });
      } else if (currency === 'item') {
        await runTransaction(db, async (transaction) => {
          const userDoc = await transaction.get(userDocRef);
          const userInventory = userDoc.data().inventory || [];
          const updatedInventory = userInventory.filter((item, index) => {
            const itemName = item.split('_').slice(1).join('_');
            return index >= amt || itemName !== selectedItem;
          });
          transaction.update(userDocRef, { inventory: updatedInventory });
        });
      }
      setAmount('');
      setSelectedItem('');
      setBankMessage('Withdrawal successful.');
      await handleUserChange(selectedUser.id);
    } catch (error) {
      console.error('Error during withdrawal:', error);
      setBankMessage('Withdrawal failed. Please try again.');
    }
  };

  return (
    <div className="page">
      <div className="page-container">
        <div className="transaction-container">
          <FaArrowLeft className="back-arrow" onClick={() => navigate(-1)} />
          <h2>💴 Bank of Hahum 💴</h2>
          {user ? (
            <div>
              <div className="user-select">
                <select
                  value={selectedUser?.id || ''}
                  onChange={(e) => handleUserChange(e.target.value)}
                  className="user-dropdown"
                >
                  <option value="" disabled>Select user</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} #{user.membershipNumber}
                    </option>
                  ))}
                </select>
              </div>
              {selectedUser && (
                <div className="balance-box">
                  <h3>{selectedUser.name}</h3>
                  <p>💴 {selectedUser.money}</p>
                  <p>🪙 {selectedUser.crypto?.hahumCoin || 0}</p>
                </div>
              )}
              <div className="transaction-form">
                <div className="currency-buttons">
                  <button
                    className={currency === 'money' ? 'active' : ''}
                    onClick={() => setCurrency('money')}
                  >
                    💴
                  </button>
                  <button
                    className={currency === 'hahumCoin' ? 'active' : ''}
                    onClick={() => setCurrency('hahumCoin')}
                  >
                    🪙
                  </button>
                  <button
                    className={currency === 'item' ? 'active' : ''}
                    onClick={() => setCurrency('item')}
                  >
                    🎁
                  </button>
                </div>
                {currency === 'item' && (
                  <select
                    value={selectedItem}
                    onChange={(e) => setSelectedItem(e.target.value)}
                    className="item-dropdown"
                  >
                    <option value="" disabled>Select item</option>
                    {items.map(item => (
                      <option key={item.id} value={item.name}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                )}
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`Enter ${currency === 'money' ? '💴' : currency === 'hahumCoin' ? '🪙' : 'quantity'} amount`}
                />
                <button onClick={handleDeposit}>Deposit to User</button>
                <button onClick={handleWithdraw}>Withdraw from User</button>
                {bankMessage && <p className="bank-message">{bankMessage}</p>}
              </div>
            </div>
          ) : (
            <p>Loading...</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Bank;
